import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Stack,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { createApiUrl } from '../../config/api';

/**
 * Public (no-auth) per-visit status page. Opened from the
 * "Property Live Update" email link. The token is a signed opaque ID.
 */
const JobStatusPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing link');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(createApiUrl(`/api/public/job-status/${encodeURIComponent(token)}`));
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'This link is invalid or has expired.');
        }
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message || 'Failed to load visit details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const lang = (data?.customer?.preferred_language || 'en').toLowerCase();
  const isSpanish = lang.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';

  const labels = isSpanish
    ? {
        title: 'Actualización de su propiedad',
        loading: 'Cargando detalles de la visita…',
        property: 'Propiedad',
        visit: 'Visita',
        serviceDate: 'Fecha de servicio',
        status: 'Estado',
        services: 'Servicios realizados',
        observations: 'Observaciones',
        files: 'Fotos y archivos',
        thanks: 'Gracias por elegir a Paramount Landscaping.',
        unsubscribe: 'Dejar de recibir estas actualizaciones',
        unsubscribeSuccess: 'Ya no recibirá estos correos.',
        unsubscribeFail: 'No se pudo procesar la baja.',
        completed: 'Completado',
        none: 'Ninguno',
        bannerAlt: 'Paramount Landscaping - actualización del servicio',
      }
    : {
        title: 'Property Live Update',
        loading: 'Loading visit details…',
        property: 'Property',
        visit: 'Visit',
        serviceDate: 'Service date',
        status: 'Status',
        services: 'Services Performed',
        observations: 'Observations',
        files: 'Photos & Files',
        thanks: 'Thank you for choosing Paramount Landscaping.',
        unsubscribe: 'Stop receiving these updates',
        unsubscribeSuccess: 'You will no longer receive these emails.',
        unsubscribeFail: 'Could not process your request.',
        completed: 'Completed',
        none: 'None',
        bannerAlt: 'Paramount Landscaping service update',
      };

  const bannerByCustomerType = {
    residential: '/live-log-banners/residential.jpg',
    multi_residential: '/live-log-banners/residential.jpg',
    commercial: '/live-log-banners/commercial.jpg',
    construction: '/live-log-banners/construction.jpg',
  };
  const bannerSrc = data?.job_type === 'winter'
    ? null
    : bannerByCustomerType[String(data?.customer?.customer_type || '').toLowerCase()];

  const fmtDate = (iso) => {
    if (!iso) return labels.none;
    const d = typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)
      ? new Date(`${iso}T12:00:00`)
      : new Date(iso);
    return d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const handleUnsubscribe = async () => {
    try {
      setUnsubscribing(true);
      const res = await fetch(createApiUrl(`/api/public/unsubscribe/${encodeURIComponent(token)}`), { method: 'POST' });
      if (!res.ok) throw new Error('Failed');
      setUnsubscribed(true);
    } catch (e) {
      setError(labels.unsubscribeFail);
    } finally {
      setUnsubscribing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', p: 3 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">{labels.loading}</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, margin: '0 auto', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ maxWidth: 680, margin: '0 auto', p: { xs: 1.5, sm: 3 } }}>
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 3, backgroundColor: '#000062', color: 'white' }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {labels.title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Paramount Landscaping
          </Typography>
        </Box>

        {bannerSrc && (
          <Box
            component="img"
            src={bannerSrc}
            alt={labels.bannerAlt}
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
            }}
          />
        )}

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Chip
            icon={<CheckIcon />}
            label={labels.completed}
            color="success"
            aria-label={labels.completed}
            sx={{ mb: 2 }}
          />

          <Stack spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {labels.property}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ ml: 4 }}>
                {data.customer?.name || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                {data.customer?.address}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TimeIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {labels.visit}
                </Typography>
              </Stack>
              <Box sx={{ ml: 4 }}>
                <Typography variant="body2">
                  <strong>{labels.serviceDate}:</strong> {fmtDate(data.visit?.scheduled_date || data.visit?.completion_date)}
                </Typography>
                {/* The live-update link is only sent in the post-completion
                    email, so this page is always rendering a completed visit
                    from the customer's perspective. Hardcoding "Completed"
                    avoids the page ever showing "Status: None" if the
                    upstream job flag is stale or temporarily flipped. */}
                <Typography variant="body2">
                  <strong>{labels.status}:</strong> {labels.completed}
                </Typography>
              </Box>
            </Box>

            {data.services_performed?.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {labels.services}
                  </Typography>
                  <List dense sx={{ pl: 2 }}>
                    {data.services_performed.map((s, i) => (
                      <ListItem key={i} sx={{ py: 0.25 }} disablePadding>
                        <ListItemText primary={s} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}

            {data.observations?.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {labels.observations}
                  </Typography>
                  <List dense sx={{ pl: 2 }}>
                    {data.observations.map((o, i) => (
                      <ListItem key={i} sx={{ py: 0.25 }} disablePadding>
                        <ListItemText
                          primary={o.title || o.note}
                          secondary={o.title ? o.note : null}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}

            {data.files?.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {labels.files}
                  </Typography>
                  {/* Image attachments render as a thumbnail grid; anything
                      else (PDFs etc.) stays a download link. */}
                  {data.files.some((f) => f.is_image) && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      {data.files.filter((f) => f.is_image).map((f, i) => (
                        <a
                          key={i}
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={f.name || labels.files}
                        >
                          <Box
                            component="img"
                            src={f.url}
                            alt={f.name || labels.files}
                            loading="lazy"
                            sx={{
                              width: '100%',
                              height: 140,
                              objectFit: 'cover',
                              borderRadius: 1,
                              display: 'block',
                            }}
                          />
                        </a>
                      ))}
                    </Box>
                  )}
                  {data.files.some((f) => !f.is_image) && (
                    <List dense sx={{ pl: 2 }}>
                      {data.files.filter((f) => !f.is_image).map((f, i) => (
                        <ListItem key={i} sx={{ py: 0.25 }} disablePadding>
                          <ListItemText
                            primary={
                              <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#de6127' }}>
                                {f.name || f.url}
                              </a>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            {labels.thanks}
          </Typography>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            {unsubscribed ? (
              <Alert severity="info">{labels.unsubscribeSuccess}</Alert>
            ) : (
              <Button
                size="small"
                variant="text"
                onClick={handleUnsubscribe}
                disabled={unsubscribing}
                aria-label={labels.unsubscribe}
              >
                {labels.unsubscribe}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default JobStatusPage;
